import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { updateUser } from "@/api/auth";
import { isLegalAcceptanceUnavailable, recordLegalAcceptance } from "@/api/legalAcceptances";
import { legalDocumentsFor } from "@/lib/legalVersions";
import { logger } from "@/lib/logger";
import { getErrorMessage } from "@/lib/getErrorMessage";
import { reportError } from "@/lib/reportError";
import { cn } from "@/lib/utils";
import { AuthCard } from "./AuthCard";
import { ValidatedField } from "./ValidatedField";
import { PasswordStrengthIndicator } from "./PasswordStrengthIndicator";
import { useFieldValidation } from "./useFieldValidation";
import { LockIcon } from "@/components/icons/status";

const passwordSchema = z
  .string()
  .min(8, "Le mot de passe doit contenir au moins 8 caractères")
  .regex(/[A-Z]/, "Le mot de passe doit contenir au moins une majuscule")
  .regex(/[0-9]/, "Le mot de passe doit contenir au moins un chiffre");

/**
 * CGU acceptées en activant le compte (mention sous le bouton). Jamais bloquant :
 * en cas d'échec, le bandeau des nouvelles conditions le rattrape.
 */
async function recordActivationAcceptance() {
  try {
    const { error } = await recordLegalAcceptance({ versions: legalDocumentsFor("user"), context: "activation" });
    if (error && !isLegalAcceptanceUnavailable(error)) reportError(error, "legal_acceptance.activation");
  } catch (err) {
    reportError(err, "legal_acceptance.activation");
  }
}

interface EmployeeSetupFormProps {
  isVerifyingToken: boolean;
  noSession: boolean;
  onSwitchMode: (mode: "login" | "forgot-password") => void;
}

export function EmployeeSetupForm({ isVerifyingToken, noSession, onSwitchMode }: EmployeeSetupFormProps) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const passwordValidation = useFieldValidation({ value: password, schema: passwordSchema });
  const confirmError =
    confirmPassword.length > 0 && confirmPassword !== password ? "Les mots de passe ne correspondent pas" : undefined;

  if (isVerifyingToken) {
    return (
      <AuthCard title="Activation en cours" subtitle="Vérification de votre invitation…">
        <div className="flex justify-center py-6">
          <Loader2 className="w-8 h-8 animate-spin text-primary" aria-hidden="true" />
        </div>
      </AuthCard>
    );
  }

  if (noSession) {
    return (
      <AuthCard title="Lien expiré">
        <div className="space-y-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto">
            <ShieldAlert className="w-7 h-7 text-destructive" aria-hidden="true" />
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Ce lien d'invitation a expiré, ou une invitation plus récente l'a remplacé. Ouvrez le dernier email
            d'invitation reçu, ou demandez à votre responsable de vous la renvoyer.
          </p>
          <p className="text-sm text-muted-foreground">
            Si vous avez déjà activé votre compte, vous pouvez{" "}
            <Button type="button" variant="link" onClick={() => onSwitchMode("login")} className="px-1 h-auto text-sm">
              vous connecter
            </Button>{" "}
            ou{" "}
            <Button
              type="button"
              variant="link"
              onClick={() => onSwitchMode("forgot-password")}
              className="px-1 h-auto text-sm"
            >
              réinitialiser votre mot de passe
            </Button>
            .
          </p>
        </div>
      </AuthCard>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    passwordValidation.markTouched();
    if (passwordValidation.state === "invalid" || !passwordSchema.safeParse(password).success || confirmError) return;

    setIsLoading(true);
    try {
      const { error } = await updateUser({ password });
      if (error) throw error;
      await recordActivationAcceptance();
      toast.success("Mot de passe défini !", { description: "Votre compte est activé. Bienvenue sur Equiply !" });
      navigate("/employee");
    } catch (err: unknown) {
      logger.error("Auth employee-setup error:", err);
      toast.error("Erreur", { description: getErrorMessage(err, "Une erreur est survenue. Veuillez réessayer.") });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthCard
      title="Activez votre compte"
      subtitle="Définissez votre mot de passe pour accéder à votre planning."
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <ValidatedField
            label="Nouveau mot de passe"
            type="password"
            leadingIcon={<LockIcon />}
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={passwordValidation.onBlur}
            disabled={isLoading}
            autoComplete="new-password"
            validation={passwordValidation}
            hideStatusIcon
          />
          <PasswordStrengthIndicator password={password} />
        </div>
        <ValidatedField
          label="Confirmer le mot de passe"
          type="password"
          leadingIcon={<LockIcon />}
          placeholder="••••••••"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isLoading}
          autoComplete="new-password"
          errorOverride={confirmError}
          hideStatusIcon
        />
        <Button
          type="submit"
          loading={isLoading}
          className={cn(
            "w-full h-12 text-base font-semibold rounded-xl mt-2",
            "bg-gradient-to-r from-primary to-accent hover:shadow-elevated hover:scale-[1.01]",
            "transition-[transform,box-shadow] duration-base ease-out-expo",
            "[&_*]:!text-primary-foreground",
          )}
        >
          {isLoading ? "Activation…" : "Activer mon compte"}
        </Button>
        <p className="text-xs text-muted-foreground leading-relaxed text-center px-2">
          En activant votre compte, vous acceptez les{" "}
          <Link to="/cgu" className="underline hover:text-foreground">
            conditions d'utilisation
          </Link>
          . Vos données sont traitées pour le compte de votre employeur, voir la{" "}
          <Link to="/confidentialite" className="underline hover:text-foreground">
            politique de confidentialité
          </Link>
          .
        </p>
      </form>
    </AuthCard>
  );
}
